/* eslint-disable prefer-const */
/* eslint-disable no-extra-boolean-cast */
import React from 'react'
import { View, Text, StyleSheet } from 'react-native'
import { Input, SelectSearch, TaskDetail, ErrorHandling, SelectOption, DAGeneralInfoService } from '@pulseops/common'
import { useTranslation } from 'react-i18next'
import { Controller, UseFormReturn, useWatch } from 'react-hook-form'
import { GeneralForm } from './da-genneral-form'
import { assets } from '@pulseops/common'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/lib/function'
// import { DAGeneralInfoService } from './da-genneral-info-service'
import * as O from 'fp-ts/lib/Option'
import { useIsFocused } from '@react-navigation/native'

type Props = {
  detail: TaskDetail.AgentOnboardNew
  form: Omit<UseFormReturn<GeneralForm.Raw>, 'handleSubmit'>
  provinceData: Array<DAGeneralInfoService.ProvinceType>
  agentGeneralData: DAGeneralInfoService.GeneralInfoLogRqData | null | undefined
  initialDistrictOptions?: SelectOption[]
  initialWardOptions: SelectOption[]
}
export const ResidentialAddress = React.memo((props: Props) => {
  const { t, i18n } = useTranslation('RequestInfo')
  const [provinceOptions, setProvinceOptions] = React.useState<SelectOption[]>([])
  const [districOptions, setDistrictOptions] = React.useState<SelectOption[]>([])
  const [wardOptions, setWardOptions] = React.useState<SelectOption[]>([])
  const { control, setValue, getValues, clearErrors } = props.form
  const { detail, provinceData, agentGeneralData } = props
  const isFocused = useIsFocused()
  const provinceVal = useWatch({
    control: control,
    name: 'ProvinceResidential'
  })
  const districtVal = useWatch({
    control: control,
    name: 'DistrictResidential'
  })
  const wardVal = useWatch({
    control: control,
    name: 'WardResidential'
  })

  React.useEffect(() => {
    if (isFocused) {
      props.initialDistrictOptions && setDistrictOptions(props.initialDistrictOptions || [])
      props.initialWardOptions && setWardOptions(props.initialWardOptions || [])
      const provinceOptions = props.provinceData.map((province) => {
        return {
          label: province.provinceCombine,
          value: province.provinceCode
        }
      })
      setProvinceOptions(provinceOptions)

    }
  }, [isFocused, props.initialWardOptions, props.initialDistrictOptions])

  const getDistricts = (provinceCode: string) => {
    setDistrictOptions([])
    pipe(
      DAGeneralInfoService.getDistricts(provinceCode),
      ZIO.map((districts) => {
        const districtOptions = districts.map((district) => {
          return {
            value: district.districtCode,
            label: i18n.language === 'vi' ? district.districtCombine : district.districtCombine
          }
        })
        setDistrictOptions(districtOptions)
        return ZIO.unit
      }),
      ErrorHandling.run()
    )
  }

  const getWards = (provinceCode: string, districtCode: string) => {
    setWardOptions([])
    pipe(
      DAGeneralInfoService.getWards({ districtCode, provinceCode }),
      ZIO.map((wards) => {
        const wardOptions = wards.map((ward) => {
          return {
            value: ward.wardCode,
            label: i18n.language === 'vi' ? ward.wardCombine : ward.wardCombine
          }
        })
        setWardOptions(wardOptions)
        return ZIO.unit
      }),
      ErrorHandling.run()
    )
  }

  const getNewWards = (provinceCode: string) => {
    setWardOptions([])
    pipe(
      DAGeneralInfoService.getNewWards(provinceCode),
      ZIO.map((wards) => {
        const wardOptions = wards.map((ward) => {
          return {
            value: ward.wardCode,
            label: i18n.language === 'vi' ? ward.wardCombine : ward.wardCombine
          }
        })
        setWardOptions(wardOptions)
        return ZIO.unit
      }),
      ErrorHandling.run()
    )
  }

  const locationCode = React.useMemo(() => {
    const provinceCode = pipe(
      O.fromNullable(provinceVal),
      O.map((provinceItem) => provinceItem.value),
      O.getOrElse(() => '')
    )
    const districtCode = pipe(
      O.fromNullable(districtVal),
      O.map((districtItem) => districtItem.value),
      O.getOrElse(() => '')
    )
    const wardCode = pipe(
      O.fromNullable(wardVal),
      O.map((wardItem) => wardItem.value),
      O.getOrElse(() => '')
    )
    return detail.hasNewProvinceCode
      ? provinceCode + wardCode
      : provinceCode + districtCode + wardCode

  }, [provinceVal, districtVal, wardVal])

  React.useEffect(() => {
    setValue('locationCodeResidential', locationCode)
  }, [locationCode])

  React.useEffect(() => {
    setValue('hasNewProvinceCode', detail.hasNewProvinceCode)
  }, [detail.hasNewProvinceCode])

  return (
    <View style={styles.containers}>
      <View style={styles.row}>
        <View style={styles.col}>
          <Text
            style={{
              fontSize: 15,
              fontWeight: '800',
              color: '#4F4F4F',
              paddingBottom: 22
            }}
          >
            {t('ResidentialAddress')}
          </Text>
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.col}>
          <Controller
            name="ProvinceResidential"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <SelectSearch
                required={true}
                popupIcon={<assets.ArrowDownDropdownIcon />}
                label={t('ProvinceResidential')}
                options={provinceOptions ?? []}
                value={value}
                onBlur={onBlur}
                onChange={(value) => {
                  if (value) {
                    onChange(value)
                    setValue('DistrictResidential', null)
                    setValue('WardResidential', null)
                    if (detail.hasNewProvinceCode) {
                      getNewWards(value.value || '')
                    } else {
                      getDistricts(value.value)
                    }
                  }
                }}
                placeholder={t('common:Select')}
                errorMessage={error?.message}
              // key={value}
              />
            )}
          />
        </View>

        <View style={styles.cols}>
          <Controller
            name="DistrictResidential"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <SelectSearch
                required={!detail.hasNewProvinceCode}
                popupIcon={<assets.ArrowDownDropdownIcon />}
                label={t('DistrictResidential')}
                options={districOptions ?? []}
                disabled={detail.hasNewProvinceCode}
                value={value}
                onBlur={onBlur}
                onChange={(value) => {
                  if (value) {
                    onChange(value)
                    setValue('WardResidential', null)
                    getWards(provinceVal?.value || '', value.value)
                  }
                }}
                placeholder={t('common:Select')}
                errorMessage={error?.message}
              // key={value}
              />
            )}
          />
        </View>
        <View style={styles.col}>
          <Controller
            name="WardResidential"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <SelectSearch
                required={true}
                popupIcon={<assets.ArrowDownDropdownIcon />}
                label={t('WardResidential')}
                options={wardOptions ?? []}
                value={value}
                onBlur={onBlur}
                onChange={(val) => {
                  onChange(val)
                  // setValue('WardResidential', val)
                }}
                placeholder={t('common:Select')}
                errorMessage={error?.message}
              // key={value}
              />
            )}
          />
        </View>
      </View>
      <View style={{ flex: 1, paddingVertical: 24 }}>
        <View style={styles.row}>
          <View style={styles.col}>
            <Controller
              name="StreetResidential"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Input
                  title={t('StreetResidential')}
                  disabled={false}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value == null ? '' : value}
                  errorMessage={error?.message}
                  maxLength={100}
                  required={true}
                />
              )}
            />
          </View>

          <View style={styles.cols}>
            <Input title={t('LocationcodeResidential')} required={true} value={locationCode || '-'} disabled />
          </View>
        </View>
      </View>
    </View>
  )
})

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignContent: 'center'
  },
  container: {
    width: '100%',
    padding: 30,
    backgroundColor: '#F4F4F4',
    borderRadius: 8,
    marginBottom: 16
  },
  containers: {
    width: '100%',
    padding: 30,
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#D3DCE6'
  },
  col: {
    width: '33.33333333333%'
  },
  cols: {
    width: '33.33333333333%',
    paddingHorizontal: 30
  },
  row: {
    flexDirection: 'row'
  },
  button: {
    borderRadius: 100,
    backgroundColor: '#ED1B2E',
    height: 36,
    width: 170,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    alignContent: 'center'
  },
  textButtonRed: { textAlign: 'center', color: '#FFF', fontWeight: 'bold' }
})
